import { BehaviorSubject } from 'rxjs'
import type { IBehaviorObservable } from '@/common/react/IBehaviorObservable'
import type { TutorialStep, TutorialStepId, TutorialTargetId } from './tutorial-steps'
import { tutorialDefinitions, type TutorialDefinition } from './tutorial-definitions'
import type { UsersService } from '@/user/UsersService.ts'
import type { UsersApiService } from '@/user/UsersApiService.ts'
import { logError } from '@/common/errors/log-utils.ts'

const devForceTutorialId = (import.meta.env.VITE_DEV_FORCE_TUTORIAL as string) || undefined

const tutorialSequence = ['main', 'collection-page']

export class TutorialService {
  public readonly currentStepId$: IBehaviorObservable<TutorialStepId | null>
  public readonly targets$: IBehaviorObservable<ReadonlyMap<TutorialTargetId, HTMLElement>>

  private readonly currentStepId: BehaviorSubject<TutorialStepId | null>
  private readonly targets: BehaviorSubject<ReadonlyMap<TutorialTargetId, HTMLElement>>
  private activeTutorial: TutorialDefinition | null = null
  private shownTutorials: Set<string> = new Set()
  private hasBootstrapped = false

  constructor(
    private readonly usersService: UsersService,
    private readonly usersApiService: UsersApiService,
  ) {
    this.currentStepId = new BehaviorSubject<TutorialStepId | null>(null)
    this.currentStepId$ = this.currentStepId
    this.targets = new BehaviorSubject<ReadonlyMap<TutorialTargetId, HTMLElement>>(new Map())
    this.targets$ = this.targets

    this.usersService.user$.subscribe((user) => {
      if (!user || this.hasBootstrapped) return
      this.hasBootstrapped = true
      this.shownTutorials = new Set(user.shownTutorials ?? [])

      if (devForceTutorialId) {
        this.startTutorial(devForceTutorialId)
      } else {
        const firstUnfinished = tutorialSequence.find((id) => !this.shownTutorials.has(id))
        if (firstUnfinished) this.startTutorial(firstUnfinished)
      }
    })
  }

  public getStep(id: TutorialStepId): TutorialStep | undefined {
    return this.activeTutorial?.steps.find((s) => s.id === id)
  }

  public getStepPosition(id: TutorialStepId): { index: number; total: number } | undefined {
    if (!this.activeTutorial) return undefined
    const index = this.activeTutorial.steps.findIndex((s) => s.id === id)
    if (index === -1) return undefined
    return { index, total: this.activeTutorial.steps.length }
  }

  public advance(): void {
    const current = this.currentStepId.value
    if (!current || !this.activeTutorial) return
    const steps = this.activeTutorial.steps
    const idx = steps.findIndex((s) => s.id === current)
    const next = steps[idx + 1]
    if (next) {
      this.currentStepId.next(next.id)
    } else {
      this.finish(this.activeTutorial.id)
    }
  }

  public advanceIfStep(stepId: TutorialStepId): void {
    if (this.currentStepId.value === stepId) {
      this.advance()
    }
  }

  public skip(): void {
    if (this.activeTutorial) {
      this.finish(this.activeTutorial.id)
    }
  }

  public registerTarget(targetId: TutorialTargetId, el: HTMLElement): void {
    const next = new Map(this.targets.value)
    next.set(targetId, el)
    this.targets.next(next)
  }

  public unregisterTarget(targetId: TutorialTargetId, el: HTMLElement): void {
    const current = this.targets.value.get(targetId)
    if (current !== el) return
    const next = new Map(this.targets.value)
    next.delete(targetId)
    this.targets.next(next)
  }

  private startTutorial(tutId: string): void {
    if (!devForceTutorialId && this.shownTutorials.has(tutId)) return
    const def = tutorialDefinitions.find((t) => t.id === tutId)
    if (!def) return
    this.activeTutorial = def
    this.currentStepId.next(def.steps[0].id)
  }

  private finish(tutId: string): void {
    this.currentStepId.next(null)
    this.activeTutorial = null
    void this.markShown(tutId)
    const nextIdx = tutorialSequence.indexOf(tutId) + 1
    if (nextIdx > 0 && nextIdx < tutorialSequence.length) {
      this.startTutorial(tutorialSequence[nextIdx])
    }
  }

  private async markShown(tutId: string): Promise<void> {
    if (this.shownTutorials.has(tutId)) return
    this.shownTutorials.add(tutId)
    const { error } = await this.usersApiService.markTutorialShown(tutId)
    if (error) {
      logError(`failed to mark tutorial ${tutId} shown: ${error}`)
    }
  }
}
